import {
  displayOrDash,
  mailRoomRequests,
  physicalFileStatusPillClass,
  type CaseSubmissionRequest,
} from '../data/mailRoom';
import { uniqueColumnSuggestions } from '../utils/columnFilter';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { wizardFlowForCategoryName } from '../utils/wizardCategoryRoute';
import { caseApiRequest } from './apiClient';

export type MailroomListResult = {
  items: CaseSubmissionRequest[];
  total: number;
  totalPages: number;
};

export type MailroomListParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

type ApiMailroomRow = {
  fileRefNo?: string;
  file_ref_no?: string;
  fileCategory?: string;
  file_category?: string;
  subFileCategory?: string;
  sub_file_category?: string;
  submittedAgency?: string;
  submitted_agency?: string;
  submittedAgencyName?: string;
  submitted_agency_name?: string;
  agencyName?: string;
  agency_name?: string;
  physicalFileStatus?: string;
  physical_file_status?: string;
  fileDeliveryMethod?: string;
  file_delivery_method?: string;
  fileMovementDateTime?: string;
  file_movement_date_time?: string;
  currentCustody?: string;
  current_custody?: string;
  caseRequestId?: string;
  case_request_id?: string;
  id?: string;
};

type ApiMailroomPage = {
  content?: ApiMailroomRow[];
  totalElements?: number;
  totalPages?: number;
};

const FILTER_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  fs: 'physicalFileStatus',
  delivery: 'fileDeliveryMethod',
  custody: 'currentCustody',
};

const SUGGESTION_VALUE: Record<string, (row: CaseSubmissionRequest) => string> = {
  ref: (r) => r.ref,
  fileCategory: (r) => r.fileCategory,
  subFileCategory: (r) => r.subFileCategory,
  agency: (r) => r.agency,
  fs: (r) => r.statusLabel,
  delivery: (r) => r.deliveryMethod,
  custody: (r) => r.custody,
};

export const SORT_COL_MAP: Record<string, string> = {
  ref: 'fileRefNo',
  fileCategory: 'fileCategory',
  subFileCategory: 'subFileCategory',
  agency: 'submittedAgency',
  fs: 'physicalFileStatus',
  delivery: 'fileDeliveryMethod',
  movementAt: 'fileMovementDateTime',
  custody: 'currentCustody',
};

function strField(row: ApiMailroomRow, ...keys: (keyof ApiMailroomRow)[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function adaptMailroomRow(row: ApiMailroomRow): CaseSubmissionRequest {
  const ref = strField(row, 'fileRefNo', 'file_ref_no');
  const statusLabel = strField(row, 'physicalFileStatus', 'physical_file_status');
  const movementRaw = strField(row, 'fileMovementDateTime', 'file_movement_date_time');
  const fileCategory = strField(row, 'fileCategory', 'file_category');
  const movementAt = movementRaw
    ? displayOrDash(formatDisplayDateTime(movementRaw) || movementRaw)
    : displayOrDash(null);
  const caseRequestId =
    strField(row, 'caseRequestId', 'case_request_id', 'id') || undefined;

  return {
    ref: displayOrDash(ref),
    fileCategory: displayOrDash(fileCategory),
    subFileCategory: displayOrDash(strField(row, 'subFileCategory', 'sub_file_category')),
    agency: displayOrDash(
      strField(
        row,
        'submittedAgency',
        'submitted_agency',
        'submittedAgencyName',
        'submitted_agency_name',
        'agencyName',
        'agency_name',
      ),
    ),
    statusLabel: displayOrDash(statusLabel),
    statusClass: physicalFileStatusPillClass(statusLabel),
    deliveryMethod: displayOrDash(strField(row, 'fileDeliveryMethod', 'file_delivery_method')),
    movementAt,
    custody: displayOrDash(strField(row, 'currentCustody', 'current_custody')),
    caseRequestId,
    flow: wizardFlowForCategoryName(fileCategory),
  };
}

function buildRequestBody(params: MailroomListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    sortBy: params.sortBy ?? 'fileRefNo',
    sortDirection: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'movementAt') {
        const day = trimmed.slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(day)) {
          body.movementFrom = `${day}T00:00:00.000Z`;
          body.movementTo = `${day}T23:59:59.999Z`;
        }
        continue;
      }
      const apiField = FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

function useMockFallback(): MailroomListResult {
  return {
    items: mailRoomRequests,
    total: mailRoomRequests.length,
    totalPages: 1,
  };
}

/** Column search auto-suggestions (min 3 characters) via list API. */
export async function fetchMailroomColumnSuggestions(
  colId: string,
  query: string,
  signal?: AbortSignal,
): Promise<string[]> {
  const extract = SUGGESTION_VALUE[colId];
  if (!extract || !FILTER_COL_MAP[colId]) return [];

  const result = await listMailroomCaseRequests({
    page: 0,
    pageSize: 20,
    columnFilters: { [colId]: query.trim() },
    signal,
  });

  return uniqueColumnSuggestions(result.items.map(extract), query);
}

/** CMS-CRC-14 — mail room case submission requests (paginated, filterable). */
export async function listMailroomCaseRequests(params: MailroomListParams = {}): Promise<MailroomListResult> {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const body = buildRequestBody(params);

  const res = await caseApiRequest<ApiMailroomPage>(
    `/mailroom-case-requests?pageNumber=${page}&pageSize=${pageSize}`,
    { method: 'POST', body, signal: params.signal },
  );

  if (!res.ok) {
    if (import.meta.env.VITE_USE_MOCK_API === 'true') return useMockFallback();
    return { items: [], total: 0, totalPages: 1 };
  }

  const content = Array.isArray(res.data?.content) ? res.data.content : [];
  const total = res.data?.totalElements ?? content.length;
  const totalPages = Math.max(1, res.data?.totalPages ?? 1);

  return {
    items: content.map(adaptMailroomRow),
    total,
    totalPages,
  };
}

/** @deprecated Use {@link listMailroomCaseRequests}. */
export async function listCaseSubmissionRequests(): Promise<
  { success: true; data: CaseSubmissionRequest[] } | { success: false; message: string }
> {
  const result = await listMailroomCaseRequests({ page: 0, pageSize: 200 });
  return { success: true, data: result.items };
}
